import React from 'react';
import { Sparklines, SparklinesCurve } from 'react-sparklines';
import {
  CommaNumber_WithDecimal,
  CommaNumber_NoDecimal,
} from '../../../util/formatter';
import YesNoLabel from '../../common/YesNoLabel';
import { NavLink } from 'react-router-dom';
import CompanyLink from '../../common/links/CompanyLink';
import { SimpleTextFilter } from '../../common/Table';
import { scopes as tableScopes } from '../../../constants/scopes';
import { useTranslation } from 'react-i18next';

export interface CompaniesListColumnsConfigProps {
  params: URLSearchParams;
  filterChange: (key: string, value: string) => void;
  companyTypes: any[];
}

function CompaniesListColumnsConfig({
  params,
  filterChange,
  companyTypes,
}: CompaniesListColumnsConfigProps) {
  const { t } = useTranslation();
  return [
    {
      Key: 'companyName',
      Label: t('Company'),
      className: 'flush',
      Render: (item: any) => (
        <div
          className="p-2 d-flex align-items-center company-td"
          style={
            item.colourHex
              ? {
                  borderLeft: `3px solid ${item.colourHex}`,
                }
              : undefined
          }
        >
          <CompanyLink
            id={item.id}
            name={item.companyName}
            imageId={item.imageId}
            imageSize={32}
            colourHex={item.colourHex}
          />
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
            testId="company-list-name-filter"
          />
        ),
      },
    },
    {
      Key: 'companyTypeName',
      Label: t('Company type'),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">{t('--- All ---')}</option>
              {companyTypes &&
                [...companyTypes]
                  ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
                  ?.map((x: any) => (
                    <option key={x.id} value={x.name}>
                      {x.name}
                    </option>
                  ))}
            </select>
          );
        },
      },
    },
    {
      Key: 'requestActivity',
      Sort: true,
      Label: t('Request activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.requestActivityScore30to60Days,
            item.requestActivityScore7to30Days,
            item.requestActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.requestActivityScore30to60Days +
                item.requestActivityScore7to30Days +
                item.requestActivityScoreLast7Days >
              0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#46b29d" />
        </Sparklines>
      ),
    },
    {
      Key: 'keyActivity',
      Sort: true,
      Label: t('Key activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.keyActivityScore30to60Days,
            item.keyActivityScore7to30Days,
            item.keyActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.keyActivityScore30to60Days +
                item.keyActivityScore7to30Days +
                item.keyActivityScoreLast7Days >
              0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#bb5ebf" />
        </Sparklines>
      ),
    },
    {
      Key: 'isProformaInvoicing',
      Label: t('Pro-forma invoicing'),
      Render: (item: any) => <YesNoLabel value={item.isProformaInvoicing} />,
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">{t('All')}</option>
              <option value="true">{t('Yes')}</option>
              <option value="false">{t('No')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'transactionLimit',
      Label: t('Max open request value'),
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_WithDecimal(item.transactionLimit, undefined),
    },
    {
      Key: 'globalTransactionLimit',
      Label: t('Max balance allocated keys'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isOverGlobalTransactionLimit ? (
          <span className="badge bg-danger">
            {CommaNumber_WithDecimal(item.globalTransactionLimit, undefined)}
          </span>
        ) : (
          CommaNumber_WithDecimal(item.globalTransactionLimit, undefined)
        ),
    },
    {
      Key: 'requestsOpen',
      Label: t('Open'),
      className: 'text-right num no-wrap',
      Render: (item: any) => (
        <NavLink
          to={`/requests/all?${tableScopes.EXCLUDEARCHIVEDTITLES}=true&${tableScopes.ISPENDING}=true&${tableScopes.COMPANY}=${item.id}`}
        >
          {CommaNumber_NoDecimal(item.requestsOpen, undefined)}
        </NavLink>
      ),
    },
    {
      Key: 'requestsComplete',
      Label: t('Complete'),
      className: 'text-right num no-wrap',
      Render: (item: any) => (
        <NavLink
          to={`/requests/all?${tableScopes.EXCLUDEARCHIVEDTITLES}=true&${tableScopes.ISAUTHORISED}=true&${tableScopes.COMPANY}=${item.id}`}
        >
          {CommaNumber_NoDecimal(item.requestsComplete, undefined)}
        </NavLink>
      ),
    },
    {
      Key: 'keysRequested',
      Label: t('Total keys'),
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_NoDecimal(item.keysRequested, undefined),
    },
  ];
}

export default CompaniesListColumnsConfig;
